'use client'

import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { ArrowLeft, Minimize2 } from 'lucide-react'
import { useEffect, useState } from 'react'

export default function HtmlFullscreenPanel() {
  const { context, clearHtmlFullscreen } = useCopilotContext()
  const [sourceUrl, setSourceUrl] = useState<string>('')

  const htmlData = context.htmlFullscreen

  useEffect(() => {
    if (!htmlData) return

    if (htmlData.content) {
      // Create a blob URL for raw HTML content
      const blob = new Blob([htmlData.content], { type: 'text/html' })
      const blobUrl = URL.createObjectURL(blob)
      setSourceUrl(blobUrl)

      // Cleanup blob URL when component unmounts or content changes
      return () => {
        URL.revokeObjectURL(blobUrl)
      }
    } else if (htmlData.url) {
      setSourceUrl(htmlData.url)
    }
  }, [htmlData])

  const handleBack = () => {
    clearHtmlFullscreen()
  }

  if (!htmlData || !sourceUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-3xl bg-white shadow-lg">
        <div className="text-gray-500">No HTML content to display</div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* Toolbar on top */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded px-3 py-1 text-sm text-gray-700 hover:bg-gray-200"
              title="Back to results"
            >
              <ArrowLeft size={16} />
              Back to Results
            </button>
            <span className="text-sm font-medium text-gray-800">{htmlData.filename}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="rounded p-2 text-gray-700 hover:bg-gray-200"
              title="Minimize"
            >
              <Minimize2 size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* HTML content iframe - takes full remaining space */}
      <div className="min-h-0 flex-1">
        <iframe
          src={sourceUrl}
          title={htmlData.filename}
          className="h-full w-full"
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          style={{
            backgroundColor: 'white',
            border: 'none',
          }}
        />
      </div>
    </div>
  )
}
